import { NavLink, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store'
import { authApi } from '../api'
import {
  LayoutDashboard, ShoppingCart, Users, Kanban, MessageCircle,
  TrendingUp, Target, Award, Gift, CheckSquare, RefreshCcw,
  BarChart2, Megaphone, DollarSign, Package, Star,
  Smartphone, Wifi, Settings, LogOut, Zap, X, KeyRound
} from 'lucide-react'

const NAV = [
  { label: 'Visão Geral', items: [
    { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/sales', icon: ShoppingCart, label: 'Vendas' },
    { to: '/clients', icon: Users, label: 'Clientes' },
  ]},
  { label: 'CRM & Comunicação', items: [
    { to: '/crm', icon: Kanban, label: 'CRM' },
    { to: '/whatsapp', icon: MessageCircle, label: 'WhatsApp' },
    { to: '/followup', icon: Zap, label: 'Follow-Up' },
    { to: '/relationship', icon: Star, label: 'Relacionamento' },
    { to: '/nps-dashboard', icon: TrendingUp, label: 'NPS' },
  ]},
  { label: 'Operacional', items: [
    { to: '/goals', icon: Target, label: 'Metas' },
    { to: '/ranking', icon: Award, label: 'Ranking' },
    { to: '/loyalty', icon: Gift, label: 'Fidelidade' },
    { to: '/tasks', icon: CheckSquare, label: 'Tarefas' },
    { to: '/exchanges', icon: RefreshCcw, label: 'Trocas' },
    { to: '/reports', icon: BarChart2, label: 'Relatórios', roles: ['admin','manager'] },
    { to: '/ads', icon: Megaphone, label: 'Anúncios', roles: ['admin','manager'] },
  ]},
  { label: 'Financeiro & Estoque', items: [
    { to: '/financial', icon: DollarSign, label: 'Financeiro', roles: ['admin','manager'] },
    { to: '/stock', icon: Package, label: 'Estoque' },
  ]},
  { label: 'Configurações', items: [
    { to: '/seller', icon: Smartphone, label: 'Modo Vendedor' },
    { to: '/whatsapp-connect', icon: Wifi, label: 'Conexão WhatsApp', roles: ['admin'] },
    { to: '/settings', icon: Settings, label: 'Configurações', roles: ['admin'] },
  ]},
]

export default function Sidebar({ isOpen, onClose }) {
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()

  const handleLogout = async () => {
    await authApi.logout().catch(() => {})
    logout()
    navigate('/login')
  }

  const handleNavClick = () => {
    // Fecha o sidebar no mobile após navegar
    if (onClose) onClose()
  }

  return (
    <>
      {/* Backdrop mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 md:hidden"
          onClick={onClose}
        />
      )}

      <aside className={`
        fixed md:sticky top-0 left-0 h-screen z-50 md:z-auto
        w-56 bg-[#12121a] border-r border-[#2a2a3a] flex flex-col overflow-y-auto flex-shrink-0
        transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `}>
        {/* Logo */}
        <div className="px-5 py-5 border-b border-[#2a2a3a] flex items-center justify-between">
          <div>
            <div className="font-display font-extrabold text-lg bg-gradient-to-r from-brand-400 to-emerald-400 bg-clip-text text-transparent">
              ERP Completo
            </div>
            <div className="text-[10px] text-[#6b6b80] font-mono mt-0.5">Sistema de Gestão</div>
          </div>
          <button onClick={onClose} className="md:hidden text-[#6b6b80] hover:text-white p-1">
            <X size={16} />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-3 px-3 space-y-1">
          {NAV.map((section) => {
            const visibleItems = section.items.filter(item => !item.roles || item.roles.includes(user?.role))
            if (!visibleItems.length) return null
            return (
              <div key={section.label} className="mb-3">
                <div className="text-[10px] font-mono text-[#6b6b80] uppercase tracking-widest px-2 pb-1.5 pt-1">
                  {section.label}
                </div>
                {visibleItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === '/'}
                    onClick={handleNavClick}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] transition-all mb-0.5 ${
                        isActive
                          ? 'bg-brand-500/15 text-brand-300 font-medium'
                          : 'text-[#6b6b80] hover:bg-[#1a1a26] hover:text-[#e8e8f0]'
                      }`
                    }
                  >
                    <item.icon size={15} />
                    {item.label}
                  </NavLink>
                ))}
              </div>
            )
          })}
        </nav>

        {/* User */}
        <div className="p-3 border-t border-[#2a2a3a]">
          <div className="flex items-center gap-2.5 px-2 py-2 rounded-lg">
            <div className="w-7 h-7 rounded-full bg-brand-500/20 flex items-center justify-center text-brand-300 text-xs font-bold flex-shrink-0">
              {user?.name?.charAt(0)?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium text-[#e8e8f0] truncate">{user?.name}</div>
              <div className="text-[10px] text-[#6b6b80] capitalize">{user?.role}</div>
            </div>
            <button
              onClick={() => { navigate('/change-password'); if (onClose) onClose() }}
              title="Alterar senha"
              className="text-[#6b6b80] hover:text-brand-300 transition-colors p-1"
            >
              <KeyRound size={13} />
            </button>
            <button onClick={handleLogout} className="text-[#6b6b80] hover:text-red-400 transition-colors p-1">
              <LogOut size={13} />
            </button>
          </div>
        </div>
      </aside>
    </>
  )
}
